<template>
  <v-row style="margin-top:.2em">
    <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2" xl="6" offset-xl="3">
      <v-card v-if="status === 'Processed'">
        <v-card-title>Completed and Processed</v-card-title>
        <v-card-text>Your previous submission has already been processed by the new student advisors, so we are unable to accept new submissions at this time. If you have any further questions or want to request additional schedule changes, please email <a href="mailto:newstudentadvising@covenant.edu" target="_blank">newstudentadvising@covenant.edu</a>.</v-card-text>
      </v-card>
      <v-card v-else-if="status === 'WD'">
        <v-card-title>Withdrawn?</v-card-title>
        <v-card-text>According to our records, you have withdrawn your application from Covenant. If this is incorrect, please contact your Admissions Counselor so we can correct your record.</v-card-text>
      </v-card>
      <v-card v-else-if="status === 'Completed'">
        <v-card-title>Submitted</v-card-title>
        <v-card-text>Your pre-registration form has been submitted. If you have any questions or need to make any changes to your submission, please email <a href="mailto:newstudentadvising@covenant.edu" target="_blank">newstudentadvising@covenant.edu</a>. Note that this will not be reflected in your Slate status page immediately but should be updated within a few days.</v-card-text>
        <v-card-actions>
          <v-btn href="https://online.covenant.edu/apply/status" text color="primary">Return to Status Page</v-btn>
        </v-card-actions>
      </v-card>
      <template v-else>
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Pre-Registration Form</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <p>Complete this form to submit your schedule requests. We will try to honor your requests as best as we can, but we cannot guarantee the classes you selected will be available.</p>
          </v-card-text>
          <v-stepper v-model="step" ref="stepper" vertical>
            <v-stepper-step :complete="step > 1" step="1">Major/Minor</v-stepper-step>
            <v-stepper-content step="1">
              <major-step :data="data" @next="next"></major-step>
            </v-stepper-content>
            <v-stepper-step :complete="step > 2" step="2">Test/Academic Credit</v-stepper-step>
            <v-stepper-content step="2">
              <test-step :data="data" :mpn="mpn" :wpn="wpn" :lpn="lpn" @back="back" @next="next"></test-step>
            </v-stepper-content>
            <v-stepper-step :complete="step > 3" step="3">Course Instructions</v-stepper-step>
            <v-stepper-content step="3">
              <course-instructions-step @back="back" @next="next"></course-instructions-step>
            </v-stepper-content>
            <v-stepper-step :complete="step > 4" step="4">Course Schedule</v-stepper-step>
            <v-stepper-content step="4">
              <course-schedule-step :major="data.major" @back="back" @next="next"></course-schedule-step>
            </v-stepper-content>
            <v-stepper-step :complete="step > 5" step="5">Comments</v-stepper-step>
            <v-stepper-content step="5">
              <comments-step :data="data" @back="back" @submit="submit"></comments-step>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import { hasRole } from '../../helpers/has'
import { reactive, ref, onMounted, computed } from '@vue/composition-api'
export default {
  components: {
    MajorStep: () => import('@/components/prereg/major'),
    TestStep: () => import('@/components/prereg/tests'),
    CourseInstructionsStep: () => import('@/components/prereg/course-instructions'),
    CourseScheduleStep: () => import('@/components/prereg/course-schedule'),
    CommentsStep: () => import('@/components/prereg/comments')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const status = ref('')
    const step = ref(1)
    const mpn = ref(0)
    const wpn = ref(null)
    const lpn = ref(null)

    const data = reactive({
      major: ''
    })
    function next (formData) {
      for (let l in formData) {
        data[l] = formData[l]
      }
      step.value++
    }
    function back () {
      if (step.value > 1) step.value--
    }
    function submit (formData) {
      for (let l in formData) {
        data[l] = formData[l]
      }
      // Create a form object with the given data
      const obj = {
        term: root.$store.state.term,
        form: 'pre-register',
        data
      }
      root.$feathers.service('orientation/form').create(obj).then(async (data) => {
        // Mark the action as completed
        for (let i = 0; i < root.$store.state.actions.length; i++) {
          if (root.$store.state.actions[i].link === '/action/pre-register') {
            let { actions } = await root.$feathers.service('orientation/tracker').patch(root.$store.state.tracker.id, { ['actions.' + i + '.done']: true })
            root.$store.commit('setActions', actions)
            break
          }
        }
        // Also put the information into the old place so it works with the CBI tracker
        let packObj = { pidm: root.$store.state.user.pidm, form: 'preregister' }
        root.$feathers.service('admissions/pack').find({ query: packObj }).then((data) => {
          if (data.length === 0) {
            packObj.details = JSON.stringify(data)
            packObj.status = 'N'
            packObj.sarchkl_updated = 'N'
            root.$feathers.service('admissions/pack').create(packObj)
          } else {
            root.$feathers.service('admissions/pack').patch(data[0].id, { details: JSON.stringify(data) })
          }
        })
        // Put up a message that their pre-registration form was submitted successfully
        root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Your pre-registration form was successfully submitted.' })
        // Redirect the student back to the home page
        root.$router.push('/')
      })
    }

    onMounted(async () => {
      if (hasRole(user.value, 'Parent')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Pre-registration is not available for accounts registered for Family Edition. Please log in with your Covenant account to pre-register for classes.' })
        root.$router.replace('/')
      }
      if (root.$store.state.term === '') await root.$store.dispatch('loadTracker')
      const rec = await root.$feathers.service('orientation/tracker').get(root.$store.state.trackerId)
      const mpnRec = rec.academics.tests.find(({ code }) => code === 'MPN')
      if (mpnRec) mpn.value = mpnRec.score
      else if ('mathLevel' in rec.academics) mpn.value = rec.academics.mathLevel
      const wpnRec = rec.academics.tests.find(({ code }) => code === 'WPN')
      if (wpnRec) wpn.value = wpnRec.score
      const { data } = await root.$feathers.service('orientation/language-placement').find({ query: { pidm: user.value.pidm } })
      if (data.length > 0) {
        lpn.value = data.map(({ language, placement }) => language + ' ' + placement).join('; ')
      }
    })

    return {
      status,
      step,
      mpn,
      wpn,
      lpn,
      data,
      next,
      back,
      submit
    }
  }
}
</script>
